import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, MessagesSquare, Pencil, FileText } from 'lucide-react';
import api, { tiposServicioAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { generatePdfCotizacion } from '../utils/pdfGenerator';

const columnas = [
    { id: 'borrador', label: 'Borrador', color: '#9ca3af' },
    { id: 'enviada', label: 'Enviada', color: '#60a5fa' },
    { id: 'aprobada', label: 'Aprobada', color: '#34d399' },
    { id: 'rechazada', label: 'Rechazada', color: '#f87171' }
];

const MisCotizaciones = ({ configuracion }) => {
    const navigate = useNavigate();
    const { user, tienePermiso } = useAuth();
    const verDetalles = tienePermiso('ver_detalles_costos');
    const verPdf = tienePermiso('ver_pdf');
    const priceClass = verDetalles ? '' : 'price-hidden';
    const blurPrice = (value, moneda) => verDetalles ? formatCurrency(value, moneda) : '•••••';
    const [cotizaciones, setCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [modalCotizacion, setModalCotizacion] = useState(null);
    const [loadingModal, setLoadingModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Alertas
    const [alertasConfig, setAlertasConfig] = useState([]);
    const [tiposActividad, setTiposActividad] = useState([]);
    const [actividadModal, setActividadModal] = useState(null);
    const [actividadForm, setActividadForm] = useState({
        tipo_actividad_id: null,
        descripcion: '',
        estado_alerta: 'comunicacion_reciente'
    });
    const [savingActividad, setSavingActividad] = useState(false);
    const [historial, setHistorial] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const [cotRes, configRes, tiposRes] = await Promise.all([
                api.cotizaciones.getMyCotizaciones(),
                api.alertas.getConfig(),
                api.alertas.getTiposActividad()
            ]);
            setCotizaciones(cotRes.data || []);
            setAlertasConfig(configRes.data || []);
            setTiposActividad((tiposRes.data || []).filter(t => t.activo));
        } catch (err) {
            console.error('Error al cargar cotizaciones:', err);
            toast.error('Error al cargar mis cotizaciones');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, moneda = 'PEN') => {
        const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
        return symbol + parseFloat(amount || 0).toLocaleString('es-PE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getAlertaColor = (clave) => {
        const config = alertasConfig.find(a => a.clave === clave);
        return config?.color || '#9ca3af';
    };

    const getAlertaNombre = (clave) => {
        const config = alertasConfig.find(a => a.clave === clave);
        return config?.nombre || clave;
    };

    const getCotizacionesByEstado = (estado) => {
        return cotizaciones.filter(cot => cot.estado === estado);
    };

    const handleDragStart = (e, cotId) => {
        if (isUpdating) { e.preventDefault(); return; }
        setDraggingId(cotId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', cotId.toString());
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setDragOverColumn(null);
    };

    const handleDragOver = (e, columnaId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverColumn !== columnaId) setDragOverColumn(columnaId);
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverColumn(null);
    };

    const handleDrop = async (e, nuevoEstado) => {
        e.preventDefault();
        const cotId = parseInt(e.dataTransfer.getData('text/plain'));
        setDraggingId(null);
        setDragOverColumn(null);
        if (isUpdating || !cotId) return;
        const cotizacion = cotizaciones.find(c => c.id === cotId);
        if (!cotizacion || cotizacion.estado === nuevoEstado) return;
        const estadoAnterior = cotizacion.estado;
        setIsUpdating(true);
        setCotizaciones(prev => prev.map(c => c.id === cotId ? { ...c, estado: nuevoEstado } : c));
        try {
            await api.cotizaciones.updateEstado(cotId, nuevoEstado);
            toast.success(`Cotizacion movida a "${columnas.find(c => c.id === nuevoEstado)?.label}"`);
        } catch (err) {
            toast.error('Error al actualizar estado');
            setCotizaciones(prev => prev.map(c => c.id === cotId ? { ...c, estado: estadoAnterior } : c));
        } finally {
            setIsUpdating(false);
        }
    };

    const handleVerCotizacion = async (cotId) => {
        setLoadingModal(true);
        try {
            const response = await api.cotizaciones.getById(cotId);
            setModalCotizacion(response.data);
        } catch (err) {
            toast.error('Error al cargar detalles');
        } finally {
            setLoadingModal(false);
        }
    };

    const closeModal = () => setModalCotizacion(null);

    const handleGenerarPdf = async (cotId, preview = false) => {
        try {
            const response = await api.cotizaciones.getById(cotId);
            const { servicios: serviciosData, extras, ...cotizacionBase } = response.data;
            const tiposRes = await tiposServicioAPI.getAll();

            const igvRate = parseFloat(configuracion?.igv_rate) || 0.18;
            const rentabilidad = parseFloat(configuracion?.rentabilidad) || 0.68;

            const servicios = (serviciosData || []).map(s => {
                const asignaciones = (s.asignaciones || []).map(a => {
                    const horas = parseFloat(a.horas) || 0;
                    const costoHora = parseFloat(a.costo_hora) || 0;
                    const horasAdicionales = horas * 0.12;
                    return { ...a, horas_adicionales: horasAdicionales, subtotal: (horas + horasAdicionales) * costoHora };
                });
                const costoServicio = asignaciones.reduce((acc, a) => acc + (parseFloat(a.subtotal) || 0), 0);
                return { ...s, asignaciones, costo: costoServicio, total: costoServicio / (1 - rentabilidad) };
            });

            const subtotalServicios = servicios.reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
            const subtotalExtras = (extras || []).reduce((acc, e) => acc + (parseFloat(e.costo) || 0), 0);
            const subtotal = subtotalServicios;
            const igv = subtotal * igvRate;
            const total = subtotal + igv + subtotalExtras;

            await generatePdfCotizacion({
                cotizacion: { ...cotizacionBase, subtotal, subtotalExtras, igv, total },
                servicios,
                extras,
                configuracion: configuracion || {},
                tiposServicio: tiposRes.data || [],
                preview
            });
        } catch (err) {
            toast.error('Error al generar PDF: ' + err.message);
        }
    };

    // ============ ACTIVIDADES ============
    const abrirActividadModal = async (cot) => {
        setActividadModal(cot);
        setActividadForm({ tipo_actividad_id: null, descripcion: '', estado_alerta: 'comunicacion_reciente' });
        setLoadingHistorial(true);
        try {
            const res = await api.alertas.getActividades(cot.id);
            setHistorial(res.data || []);
        } catch (err) {
            setHistorial([]);
        } finally {
            setLoadingHistorial(false);
        }
    };

    const cerrarActividadModal = () => {
        setActividadModal(null);
        setHistorial([]);
    };

    const guardarActividad = async () => {
        if (!actividadForm.tipo_actividad_id) {
            toast.error('Selecciona un tipo de actividad');
            return;
        }
        setSavingActividad(true);
        try {
            const res = await api.alertas.registrarActividad(actividadModal.id, actividadForm);
            setHistorial(prev => [res.data, ...prev]);
            setCotizaciones(prev => prev.map(c =>
                c.id === actividadModal.id
                    ? { ...c, ultima_alerta_estado: actividadForm.estado_alerta, ultima_alerta_fecha: new Date().toISOString(), ultima_actividad_tipo: res.data.tipo_nombre, total_actividades: (c.total_actividades || 0) + 1 }
                    : c
            ));
            setActividadForm({ tipo_actividad_id: null, descripcion: '', estado_alerta: 'comunicacion_reciente' });
            toast.success('Actividad registrada');
        } catch (err) {
            toast.error('Error al registrar actividad');
        } finally {
            setSavingActividad(false);
        }
    };

    if (loading) {
        return <div className="loading">Cargando mis cotizaciones...</div>;
    }

    return (
        <div className="kanban-container">
            <div className="kanban-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Mis Cotizaciones</h2>
                        <span style={{ color: 'var(--muted)', fontSize: '14px' }}>{user?.nombre}</span>
                    </div>
                </div>
                <span style={{ color: 'var(--muted)', fontSize: '14px' }}>
                    {cotizaciones.length} cotizacion{cotizaciones.length !== 1 ? 'es' : ''}
                </span>
            </div>

            <div className="kanban-board">
                {columnas.map((columna) => {
                    const items = getCotizacionesByEstado(columna.id);
                    const isOver = dragOverColumn === columna.id;
                    return (
                        <div
                            key={columna.id}
                            className={`kanban-column ${isOver ? 'drag-over' : ''}`}
                            onDragOver={(e) => handleDragOver(e, columna.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, columna.id)}
                        >
                            <div className="kanban-column-header" style={{ '--column-color': columna.color }}>
                                <span className="kanban-column-title">{columna.label}</span>
                                <span className="kanban-column-count">{items.length}</span>
                            </div>
                            <div className="kanban-column-body">
                                {items.length === 0 ? (
                                    <div className="kanban-empty">Sin cotizaciones</div>
                                ) : (
                                    items.map((cot) => (
                                        <div
                                            key={cot.id}
                                            className={`kanban-card ${draggingId === cot.id ? 'dragging' : ''} ${isUpdating ? 'updating' : ''}`}
                                            draggable={!isUpdating}
                                            onDragStart={(e) => handleDragStart(e, cot.id)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <div className="kanban-card-header">
                                                <div className="kanban-card-numero-wrap">
                                                    <span className="kanban-card-numero">{cot.numero}</span>
                                                    {cot.cliente_empresa && (
                                                        <span className="kanban-card-empresa-badge">
                                                            {cot.cliente_empresa.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="kanban-card-fecha">
                                                    {new Date(cot.fecha || cot.created_at).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </span>
                                            </div>
                                            <div className="kanban-card-cliente">
                                                {cot.cliente_nombre}
                                            </div>
                                            <div className={`kanban-card-total ${priceClass}`}>
                                                {blurPrice(cot.total, cot.moneda)}
                                            </div>

                                            {/* Indicador de alerta - solo para "enviada" */}
                                            {(cot.ultima_alerta_estado || cot.total_actividades > 0) && (
                                                <div className="kanban-card-separator" />
                                            )}
                                            {cot.estado === 'enviada' && cot.ultima_alerta_estado && (
                                                <div className="kanban-card-alerta">
                                                    <span
                                                        className="kanban-alerta-dot"
                                                        style={{ backgroundColor: getAlertaColor(cot.ultima_alerta_estado) }}
                                                    />
                                                    <span className="kanban-alerta-text">
                                                        {getAlertaNombre(cot.ultima_alerta_estado)}
                                                        {cot.ultima_actividad_tipo && ` - ${cot.ultima_actividad_tipo}`}
                                                    </span>
                                                </div>
                                            )}
                                            {cot.total_actividades > 0 && (
                                                <div className="kanban-card-ultima-actividad">
                                                    Última actividad: {cot.ultima_actividad_fecha && new Date(cot.ultima_actividad_fecha).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    <span className="actividad-count-text"> - {cot.total_actividades} {cot.total_actividades === 1 ? 'registro' : 'registros'}</span>
                                                </div>
                                            )}

                                            <div className="kanban-card-actions">
                                                <button
                                                    className="kanban-action-btn kanban-action-view"
                                                    onClick={(e) => { e.stopPropagation(); handleVerCotizacion(cot.id); }}
                                                    title="Ver detalles"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                {cot.estado === 'enviada' && (
                                                    <button
                                                        className="kanban-action-btn kanban-action-seguimiento"
                                                        onClick={(e) => { e.stopPropagation(); abrirActividadModal(cot); }}
                                                        title="Seguimiento"
                                                    >
                                                        <MessagesSquare size={16} />
                                                    </button>
                                                )}
                                                {verPdf && (
                                                    <button
                                                        className="kanban-action-btn kanban-action-pdf"
                                                        onClick={(e) => { e.stopPropagation(); handleGenerarPdf(cot.id, cot.estado === 'borrador'); }}
                                                        title={cot.estado === 'borrador' ? 'Previsualizar PDF' : 'Descargar PDF'}
                                                    >
                                                        <FileText size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    className="kanban-action-btn kanban-action-edit"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/cotizaciones/${cot.id}`); }}
                                                    title="Editar"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modal de vista de cotizacion */}
            {(modalCotizacion || loadingModal) && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
                        {loadingModal ? (
                            <div className="loading">Cargando detalles...</div>
                        ) : modalCotizacion && (
                            <>
                                <div className="modal-header">
                                    <div>
                                        <h3 style={{ margin: 0 }}>Cotizacion {modalCotizacion.numero}</h3>
                                        <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                                            {new Date(modalCotizacion.fecha || modalCotizacion.created_at).toLocaleDateString('es-PE')}
                                        </span>
                                    </div>
                                    <button className="modal-close" onClick={closeModal}>&times;</button>
                                </div>
                                <div className="modal-body">
                                    <div className="cotizacion-detail-grid">
                                        <div className="detail-section">
                                            <h4>Cliente</h4>
                                            <p><strong>{modalCotizacion.cliente_nombre}</strong></p>
                                            {modalCotizacion.cliente_empresa && <p>{modalCotizacion.cliente_empresa}</p>}
                                            {modalCotizacion.cliente_email && <p>{modalCotizacion.cliente_email}</p>}
                                        </div>
                                        <div className="detail-section">
                                            <h4>Estado</h4>
                                            <span className={`badge estado-${modalCotizacion.estado}`}>
                                                {columnas.find(c => c.id === modalCotizacion.estado)?.label || modalCotizacion.estado}
                                            </span>
                                        </div>
                                    </div>
                                    {modalCotizacion.servicios && modalCotizacion.servicios.length > 0 && (
                                        <div className="detail-section">
                                            <h4>Servicios</h4>
                                            {modalCotizacion.servicios.map((serv, idx) => (
                                                <div key={idx} className="service-detail-card">
                                                    <div className="service-detail-header">
                                                        <span className="service-name">{serv.tipo_servicio_nombre}</span>
                                                        <span className={`service-total ${priceClass}`}>{blurPrice(serv.total, modalCotizacion.moneda)}</span>
                                                    </div>
                                                    {serv.asignaciones && serv.asignaciones.length > 0 && (
                                                        <div className="service-breakdown">
                                                            <span className="breakdown-label">Personal:</span>
                                                            {serv.asignaciones.map((a, i) => {
                                                                const horas = parseFloat(a.horas) || 0;
                                                                if (!verDetalles) {
                                                                    return (
                                                                        <div key={i} className="breakdown-item">
                                                                            <span>{a.personal_nombre}</span>
                                                                            <span className={priceClass}>{horas}h = {blurPrice(a.subtotal, modalCotizacion.moneda)}</span>
                                                                        </div>
                                                                    );
                                                                }
                                                                const rent = a.rentabilidad !== undefined && a.rentabilidad !== null
                                                                    ? parseFloat(a.rentabilidad)
                                                                    : 0.68;
                                                                const costoHora = parseFloat(a.costo_hora) || 0;
                                                                const horasAdicionales = parseFloat(a.horas_adicionales) || (horas * 0.12);
                                                                const costo = parseFloat(a.subtotal) || 0;
                                                                const total = rent > 0 && rent < 1 ? costo / (1 - rent) : costo;
                                                                const ganancia = total - costo;
                                                                return (
                                                                    <div key={i} className="breakdown-item">
                                                                        <span>{a.personal_nombre}</span>
                                                                        <span>
                                                                            {horas}h
                                                                            {horasAdicionales > 0 && <span className="adicional-text"> +{horasAdicionales.toFixed(2)}</span>}
                                                                            {' '}x {formatCurrency(costoHora, modalCotizacion.moneda)} = {formatCurrency(costo, modalCotizacion.moneda)}
                                                                            <span className="ganancia-text"> + {formatCurrency(ganancia, modalCotizacion.moneda)}</span>
                                                                            <span className="total-text"> = {formatCurrency(total, modalCotizacion.moneda)}</span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {serv.costosFijos && serv.costosFijos.length > 0 && (
                                                        <div className="service-breakdown">
                                                            <span className="breakdown-label">Costos Fijos:</span>
                                                            {serv.costosFijos.map((cf, i) => {
                                                                const rent = cf.rentabilidad !== undefined && cf.rentabilidad !== null
                                                                    ? parseFloat(cf.rentabilidad)
                                                                    : 0.68;
                                                                const costo = parseFloat(cf.costo) || 0;
                                                                const total = rent > 0 && rent < 1 ? costo / (1 - rent) : costo;
                                                                if (!verDetalles) {
                                                                    return (
                                                                        <div key={i} className="breakdown-item">
                                                                            <span>{cf.costo_fijo_nombre}</span>
                                                                            <span className={priceClass}>{blurPrice(cf.subtotal, modalCotizacion.moneda)}</span>
                                                                        </div>
                                                                    );
                                                                }
                                                                const ganancia = total - costo;
                                                                return (
                                                                    <div key={i} className="breakdown-item">
                                                                        <span>{cf.costo_fijo_nombre}</span>
                                                                        <span>
                                                                            {formatCurrency(costo, modalCotizacion.moneda)}
                                                                            <span className="ganancia-text"> + {formatCurrency(ganancia, modalCotizacion.moneda)}</span>
                                                                            <span className="total-text"> = {formatCurrency(total, modalCotizacion.moneda)}</span>
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {modalCotizacion.extras && modalCotizacion.extras.length > 0 && (
                                        <div className="detail-section">
                                            <h4>Extras</h4>
                                            <div className="detail-table-wrap">
                                                <table className="detail-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Detalle</th>
                                                            <th style={{ textAlign: 'right' }}>Costo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {modalCotizacion.extras.map((extra, idx) => (
                                                            <tr key={idx}>
                                                                <td>{extra.detalle}</td>
                                                                <td style={{ textAlign: 'right' }} className={priceClass}>{blurPrice(extra.costo, modalCotizacion.moneda)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {verDetalles && (
                                        <div className="color-legend">
                                            <span className="legend-title">Leyenda:</span>
                                            <span className="legend-item"><span className="legend-dot amarillo"></span>12% Tiempo adicional</span>
                                            <span className="legend-item"><span className="legend-dot verde"></span>Ganancia</span>
                                            <span className="legend-item"><span className="legend-dot azul"></span>Total (personal y costo fijo)</span>
                                        </div>
                                    )}

                                    <div className={`detail-totals ${priceClass}`}>
                                        <div className="total-row">
                                            <span>Subtotal</span>
                                            <span>{blurPrice(modalCotizacion.subtotal, modalCotizacion.moneda)}</span>
                                        </div>
                                        {modalCotizacion.descuento > 0 && (
                                            <div className="total-row">
                                                <span>Descuento</span>
                                                <span>-{blurPrice(modalCotizacion.descuento, modalCotizacion.moneda)}</span>
                                            </div>
                                        )}
                                        <div className="total-row">
                                            <span>IGV ({(modalCotizacion.igv_rate || 0.18) * 100}%)</span>
                                            <span>{blurPrice(modalCotizacion.igv, modalCotizacion.moneda)}</span>
                                        </div>
                                        <div className="total-row total-final">
                                            <span>Total</span>
                                            <span>{blurPrice(modalCotizacion.total, modalCotizacion.moneda)}</span>
                                        </div>
                                    </div>

                                    {modalCotizacion.comentarios && (
                                        <div className="detail-section" style={{ marginTop: '16px' }}>
                                            <h4>Comentarios</h4>
                                            <div style={{ color: 'var(--muted)', fontSize: '13px' }} dangerouslySetInnerHTML={{ __html: modalCotizacion.comentarios }} />
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button className="btn ghost" onClick={closeModal}>Cerrar</button>
                                    <button className="btn primary" onClick={() => navigate(`/cotizaciones/${modalCotizacion.id}`)}>
                                        Editar Cotizacion
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Modal de registrar actividad / historial */}
            {actividadModal && (
                <div className="modal-overlay" onClick={cerrarActividadModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
                        <div className="modal-header">
                            <div>
                                <h3 style={{ margin: 0 }}>{tienePermiso('seguimientos') ? 'Registrar Actividad' : 'Seguimiento'}</h3>
                                <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                                    {actividadModal.numero} - {actividadModal.cliente_nombre}
                                </span>
                            </div>
                            <button className="modal-close" onClick={cerrarActividadModal}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {tienePermiso('seguimientos') && (
                            <div className="actividad-form">
                                <label className="label">Tipo de actividad</label>
                                <div className="actividad-tipo-grid">
                                    {tiposActividad.map(tipo => (
                                        <button
                                            key={tipo.id}
                                            className={`actividad-tipo-btn ${actividadForm.tipo_actividad_id === tipo.id ? 'selected' : ''}`}
                                            onClick={() => setActividadForm(prev => ({ ...prev, tipo_actividad_id: tipo.id }))}
                                        >
                                            {tipo.nombre}
                                        </button>
                                    ))}
                                </div>

                                <label className="field">
                                    <span className="label">Descripcion breve</span>
                                    <input
                                        className="input"
                                        type="text"
                                        value={actividadForm.descripcion}
                                        onChange={(e) => setActividadForm(prev => ({ ...prev, descripcion: e.target.value }))}
                                        placeholder="Ej: Se conversó sobre la propuesta, queda en revisar"
                                        maxLength={500}
                                    />
                                </label>

                                <label className="label">Estado de seguimiento</label>
                                <div className="actividad-estado-btns">
                                    {alertasConfig.filter(a => a.clave === 'comunicacion_reciente' || a.clave === 'pendiente_respuesta').map(alerta => (
                                        <button
                                            key={alerta.id}
                                            className={`actividad-estado-btn ${actividadForm.estado_alerta === alerta.clave ? 'selected' : ''}`}
                                            style={{ '--estado-color': alerta.color }}
                                            onClick={() => setActividadForm(prev => ({ ...prev, estado_alerta: alerta.clave }))}
                                        >
                                            <span className="actividad-estado-dot" style={{ backgroundColor: alerta.color }} />
                                            {alerta.nombre}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="btn neon"
                                    onClick={guardarActividad}
                                    disabled={savingActividad}
                                    style={{ marginTop: '4px' }}
                                >
                                    {savingActividad ? 'Guardando...' : 'Registrar Actividad'}
                                </button>
                            </div>
                            )}

                            {/* Historial */}
                            <div style={{ marginTop: '20px' }}>
                                <h4 style={{ color: 'var(--text)', marginBottom: '8px' }}>Historial de actividades</h4>
                                {loadingHistorial ? (
                                    <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Cargando...</p>
                                ) : historial.length === 0 ? (
                                    <p style={{ color: 'var(--muted)', fontSize: '13px' }}>Sin actividades registradas</p>
                                ) : (
                                    <div className="actividad-historial">
                                        {historial.map(act => (
                                            <div key={act.id} className="actividad-item">
                                                <div className="actividad-item-dot" />
                                                <div className="actividad-item-content">
                                                    <div className="actividad-item-tipo">{act.tipo_nombre}</div>
                                                    {act.descripcion && (
                                                        <div className="actividad-item-desc">{act.descripcion}</div>
                                                    )}
                                                    <div className="actividad-item-meta">
                                                        {act.usuario_nombre} - {new Date(act.created_at).toLocaleDateString('es-PE')} {new Date(act.created_at).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MisCotizaciones;
